import React, { useEffect, useRef, useState } from 'react';
import { useT } from '@/i18n/labels';

interface SpamConfirmDialogProps {
  /** True while the POST is in flight: both buttons and Escape are inert so it cannot be double-sent. */
  busy: boolean;
  /** The server's error text (or the console's own generic one) when the last attempt failed. */
  error: string | null;
  /** Fired by Confirm with the checkbox value. The caller performs the POST. */
  onConfirm: (blockSender: boolean) => void;
  onCancel: () => void;
}

/**
 * OP-1005 - the confirm dialog for "Spam / unwanted".
 *
 * Built into the page, NOT `window.confirm`: the host the console runs in blocks native dialogs, and
 * a blocked `confirm()` returns false without ever showing anything - so a native confirm here would
 * make the action silently impossible.
 *
 * Confirming ends in a permanent delete after the grace window, so:
 *  - the default focus is on Cancel, not Confirm;
 *  - "Also block this sender for this channel" is UNCHECKED every time the dialog opens (it is a
 *    separate, consequential choice and is never remembered from a previous conversation);
 *  - nothing is sent until Confirm is pressed.
 *
 * Rendered inline (not through a portal) so it inherits the `.ac-root` `dir`: the Hebrew layout stays
 * right-to-left. Layout uses logical properties only.
 */
export const SpamConfirmDialog: React.FC<SpamConfirmDialogProps> = ({ busy, error, onConfirm, onCancel }) => {
  const { t } = useT();
  const [blockSender, setBlockSender] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    cancelRef.current?.focus();
  }, []);

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      if (!busy) onCancel();
      return;
    }
    if (e.key !== 'Tab') return;
    // Keep Tab inside the dialog: the page behind it is not interactive while it is open.
    const focusable = dialogRef.current?.querySelectorAll<HTMLElement>(
      'input:not([disabled]), button:not([disabled])',
    );
    if (!focusable || focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  return (
    <div className="ac-modal-backdrop" data-testid="spam-dialog-backdrop">
      <div
        ref={dialogRef}
        className="ac-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="spam-dialog-title"
        aria-describedby="spam-dialog-body"
        data-testid="spam-dialog"
        onKeyDown={onKeyDown}
      >
        <h2 id="spam-dialog-title" className="ac-modal-title">
          {t('spam_dialog_title')}
        </h2>
        <p id="spam-dialog-body" className="ac-modal-body">
          {t('spam_dialog_body')}
        </p>
        <label className="ac-modal-check">
          <input
            type="checkbox"
            checked={blockSender}
            disabled={busy}
            onChange={(e) => setBlockSender(e.target.checked)}
            data-testid="spam-block-sender"
          />
          <span>{t('spam_block_sender')}</span>
        </label>

        {error && (
          <div role="alert" className="ac-modal-error" data-testid="spam-error">
            {error}
          </div>
        )}

        <div className="ac-modal-actions">
          <button
            ref={cancelRef}
            type="button"
            className="ac-btn ghost sm"
            onClick={onCancel}
            disabled={busy}
            data-testid="spam-cancel"
          >
            {t('spam_cancel')}
          </button>
          <button
            type="button"
            className="ac-btn danger sm"
            onClick={() => onConfirm(blockSender)}
            disabled={busy}
            data-testid="spam-confirm"
          >
            {busy ? t('spam_working') : t('spam_confirm')}
          </button>
        </div>
      </div>
    </div>
  );
};
